import { useState, useEffect, useMemo } from 'react';
import ReactImage from '@4f2e/react-image';
import cx from 'classnames';
import { supportWebpDomain } from '@/constants/url'; // 支持webp的图片域名
import { convertToWebP } from '@/utils/image';
import Styles from './index.module.scss';

// React-image组件: https://code.djicorp.com/fe/react-image

const defaultDevice = require('@/assets/icon/default-device.png');
const defaultAvatar = `${process.env.RESOURCE_URL}uc_server/images/noavatar_middle.gif`;
export const defaultImage = require('@/assets/icon/defaultCover.png');


export enum CutModeEnum {
  cover = 'cover',
  contain = 'contain',
  fill = 'fill',
  none = 'none',
}

export enum ImageType {
  avatar = 'avatar',
  image = 'image',
  icon = 'icon',
  device = 'device',
}

/*
  react-image模糊、放大、骨架屏等自定义样式
*/
export interface ImageSlots {
  wrapper?: string; // img父容器类名
  picture?: string; // 响应式图片picture元素类名
  img?: string; // img元素类名
  zoomedWrapper?: string; // 图片放大时img父容器类名
  blurredImg?: string; // 模糊效果img元素类名
  skeletonWrapper?: string; // 骨架屏img父容器类名
  skeletonImg?: string; // 骨架屏img元素类名
}

export interface ICommontReactImage {
  className?: string;
  classNames?: ImageSlots;
  src: string;
  alt?: string;
  type?: ImageType;
  cutMode?: keyof typeof CutModeEnum;
  style?: React.CSSProperties;
  disableSkeleton?: boolean;  // 是否禁用骨架屏
  ratio?: string;  // webp图片压缩比例
  disableWebp?: boolean;  // 禁用webp模式
  removeWrapper?: boolean; // 是否移除img外层容器，为true时会禁用放大、降级url、骨架屏和渐进加载效果
  onError?: ()=>void;
  onClick?: ()=>void;
  onLoad?: (item: any)=>void;
}

const CommontReactImage = ({
  src,
  className = '',
  classNames,
  alt = '大疆社区',
  type,
  cutMode,
  style,
  disableSkeleton = false,
  ratio,
  disableWebp,
  removeWrapper,
  onError,
  onClick,
  onLoad,
}: ICommontReactImage) => {
  // 拼接webp后缀
  const webpSrc = convertToWebP(src, ratio);
  // webp模式实际显示的图片资源
  const [currentSrc, setCurrentSrc] = useState(webpSrc);

  // 普通模式中, 图片加载失败时的兜底图片
  const [fallback, setFallback] = useState('');

  useEffect(() => {
    setCurrentSrc(webpSrc);
  }, [webpSrc])

  const isWebpSupported = useMemo(() => {
    if (!src) return false;
    // 判断当前src的域名是否在支持webp的域名列表中
    const domain = new URL(src).hostname;
    return supportWebpDomain.includes(domain);
  }, [src])

  const imageStyle = useMemo(() => {
    if (cutMode) {
      return {
        objectFit: cutMode,
      };
    }
  }, [cutMode]);

  const handleError = () => {
    // 普通模式使用fallback来兜底
    if (type === ImageType.avatar) {
      setFallback(defaultAvatar);
    } else if (type === ImageType.device) {
      setFallback(defaultDevice?.default?.src);
    } else {
      setFallback(defaultImage?.default?.src);
    }
    onError?.();
  }

  const handleSourceError = () => {
    if (currentSrc === webpSrc) {
      // 第一轮: webp图片加载失败,替换为普通图片
      setCurrentSrc(src as string);
    } else if (currentSrc === src) {
      // 第二轮: 普通图片加载失败,替换为默认图片
      if (type === ImageType.avatar) {
        setCurrentSrc(defaultAvatar);
      } else if (type === ImageType.device) {
        setCurrentSrc(defaultDevice?.default?.src);
      } else {
        setCurrentSrc(defaultImage?.default?.src);
      }
      onError?.();
    }
  };

  // webp模式
  if (isWebpSupported && !disableWebp) {
    return (
      <ReactImage
        responsiveImages={{
          sourceConfig: [
            {
              type: 'image/webp',
              srcSet: currentSrc,
            },
          ],
          defaultUrl: src as string,
        }}
        classNames={{
          wrapper: Styles.imageWrapper,
          img: cx(Styles.image, className),
          skeletonImg: Styles.skeletonImg,
          skeletonWrapper: Styles.skeletonWrapper,
          ...classNames,
        }} 
        alt={alt}
        style={{...imageStyle, ...style}}
        disableSkeleton={disableSkeleton}
        removeWrapper={removeWrapper}
        onError={handleSourceError}
        onClick={onClick}
        onLoad={onLoad}
      />
    )
  }

  // 普通模式
  return (
    <ReactImage
      src={src}
      classNames={{
        wrapper: Styles.imageWrapper,
        img: cx(Styles.image, className),
        skeletonImg: Styles.skeletonImg,
        skeletonWrapper: Styles.skeletonWrapper,
        ...classNames,
      }}
      alt={alt}
      style={{...imageStyle, ...style}}
      fallbackSrc={fallback}
      disableSkeleton={disableSkeleton}
      removeWrapper={removeWrapper}
      onError={handleError}
      onClick={onClick}
      onLoad={onLoad}
    />
  );
};

export default CommontReactImage;
